import { FC, MutableRefObject, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { EditorContext } from '@editor/contexts/Editor'
import useShape from '@editor/hooks/Shape'
import { LabelProps, PolygonPlaneMeshProps, ShapeKeyType } from '@editor/interfaces/interfaces'
import { alignToZAxis, getPolygonFromPoints } from '@editor/services/Shape'
import { meterToMillimeter } from '@editor/services/Util'
import { RootState } from '@editor/store/app'
import { StylesProvider } from '@infraloop/common'
import { Html } from '@react-three/drei'
import { merge } from 'lodash'
import polylabel from 'polylabel'
import { useSelector } from 'react-redux'
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  ShapeUtils,
  Vector2,
  Vector3,
} from 'three'

import Polyline from './Polyline'

export const PolygonPlaneMesh: FC<PolygonPlaneMeshProps> = ({
  id,
  name,
  polygon,
  offsetTransformation,
  origin,
  transparent = true,

  color,
  opacity = 1,

  label,

  guideOpacity = 1,

  guideLine,
  guideOutline,
  guideLabel,

  selectable,
  isOpenLoop = false,
  depthTest = true,
  depthWrite = true,
  renderOrder = 1,

  isWorking = false,
  visible = true,
}) => {
  // Context
  const { updateMeshRefs, cameraRef } = useContext(EditorContext)

  // Refs
  const meshRef = useRef<Mesh | null>(null)

  // Store
  const labelFontSize = useSelector((state: RootState) => state.toolUtil.core.labelFontSize)
  const currentAnchorPosition = useSelector((state: RootState) => state.toolUtil.core.currentAnchorPosition)
  const currentFillColor = useSelector((state: RootState) => state.toolUtil.core.currentFillColor)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Vars
  const offsetMatrix = useMemo(() => {
    if (offsetTransformation instanceof Matrix4) return offsetTransformation
    return offsetTransformation ? new Matrix4().fromArray(offsetTransformation) : new Matrix4().identity()
  }, [offsetTransformation])
  const planeOrigin = useMemo(
    () => (origin ? new Vector3(...origin) : new Vector3(...polygon.center!)),
    [origin, polygon.center],
  )
  const planePoints = useMemo(
    () =>
      [...polygon.positions, ...(currentAnchorPosition && isWorking ? [currentAnchorPosition] : [])].map((p) =>
        new Vector3(...p).applyMatrix4(offsetMatrix),
      ),
    [polygon.positions, isWorking, currentAnchorPosition, offsetMatrix],
  )
  const planeVertices = useMemo(() => {
    // Non-working polygon use provided vertices as-is.
    if (!isWorking) return polygon.vertices.map((v) => new Vector2(...v))

    // If working, we need to add the current anchor and recalculate the vertices.
    // Generate vertices once we actually have a triangle.
    const { vertices } = planePoints.length >= 3 ? getPolygonFromPoints(planePoints) : { vertices: [] }
    return vertices.map((v) => new Vector2(...v))
  }, [polygon.vertices, planePoints, isWorking])
  const planeCenter = useMemo(() => new Vector3(...polygon.center!), [polygon.center])
  const labelPosition = useMemo(() => {
    const { points, inverseMatrix, offset } = alignToZAxis(
      polygon.positions.map((v) => new Vector3(...v)),
      true,
    )

    const centered = polylabel([points.map((pos) => [pos.x, pos.y])], 0.001)
    return new Vector3(centered[0], centered[1], points[0].z)
      .applyMatrix4(inverseMatrix)
      .add(offset)
      .applyMatrix4(offsetMatrix)
      .toArray()
  }, [polygon, offsetMatrix])

  // Hooks
  const { onPointerDown, onPointerUp, onPointerOver, onPointerOut } = useShape(polygon, ShapeKeyType.POLYGON, cameraRef)

  // Label Configs
  const labelConfig = useMemo(
    (): PolygonPlaneMeshProps['label'] =>
      label
        ? merge(
            {},
            {
              color: 'black',
              backgroundColor: 'yellow',
              fontSize: labelFontSize,
            },
            label,
          )
        : undefined,
    [label, labelFontSize],
  )

  // Guide Configs
  // For as long as any of guideLabel, guideLine, or guideOutline is defined, guides will be shown.
  const guideLineConfig = useMemo(
    (): PolygonPlaneMeshProps['guideLine'] =>
      guideLine || guideOutline || guideLabel ? merge({}, guideLine) : undefined,
    [guideLine, guideOutline, guideLabel],
  )
  const guideOutlineConfig = useMemo(
    (): PolygonPlaneMeshProps['guideOutline'] => (guideOutline ? merge({}, guideOutline) : undefined),
    [guideOutline],
  )
  const guideLabelConfig = useMemo(
    (): PolygonPlaneMeshProps['guideLabel'] =>
      guideLabel
        ? merge(
            {},
            {
              color: '#000',
              backgroundColor: '#fff',
            },
            guideLabel,
          )
        : undefined,
    [guideLabel],
  )
  const guideLabels = useMemo((): LabelProps[] => {
    if (!guideLabelConfig || planePoints.length < 2) return []

    // Calculate the positions and label for the labels on midpoints of the edges.
    const points = isOpenLoop ? planePoints : planePoints.slice(0, -1)
    let positions = points.map((point, index) => {
      const nextPoint = points[(index + 1) % points.length]
      return {
        point: point.clone().add(nextPoint).multiplyScalar(0.5).toArray(),
        label: `${Math.round(meterToMillimeter(new Vector3(...point).distanceTo(nextPoint)))}mm`,
      }
    })

    // Hide the last label if hideLastLabel is true
    if (guideLabelConfig.hideLastLabel && positions.length > 0) {
      positions = positions.slice(0, -1)
    }

    return positions.map(({ point, label: lbl }, index) => ({
      ...guideLabelConfig,
      id: `${polygon.shape_id}-label-${index}`,
      point,
      label: lbl,
      labelPrefix: '',
    }))
  }, [planePoints, guideLabelConfig, isOpenLoop, polygon.shape_id])

  /**
   * Keep track of the mesh reference
   */
  useEffect(() => {
    if (polygon.shape_id) {
      updateMeshRefs({ [polygon.shape_id]: meshRef as MutableRefObject<Object3D> })
    }

    return () => {
      updateMeshRefs({ [polygon.shape_id]: null })
    }
  }, [updateMeshRefs, polygon.shape_id])

  const [geometry, setGeometry] = useState<BufferGeometry>(new BufferGeometry())

  useEffect(() => () => geometry && geometry.dispose(), [geometry])

  useEffect(() => {
    if (!planePoints?.length || !planeVertices?.length || !planeCenter) return

    const buffer = new BufferGeometry()
    const positions = new Float32Array(planePoints.length * 3)
    planePoints.forEach((vertex, index) => {
      vertex.toArray(positions, index * 3)
    })

    const indi = ShapeUtils.triangulateShape(planeVertices, [])

    buffer.setAttribute('position', new BufferAttribute(positions, 3))
    buffer.setIndex(indi.flat())
    buffer.translate(-planeCenter.x, -planeCenter.y, -planeCenter.z)
    buffer.computeVertexNormals()
    setGeometry(buffer)
  }, [planePoints, planeVertices, planeCenter])

  /**
   * Update the mesh material color when the current fill color changes.
   * This is used for real-time updates of the fill color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the `color` prop.
   */
  useEffect(() => {
    if (
      !meshRef.current ||
      !(meshRef.current.material instanceof MeshBasicMaterial) ||
      !currentFillColor ||
      (!selectedElementIds.includes(id) && !isWorking)
    )
      return

    meshRef.current.visible = currentFillColor !== 'transparent'
    meshRef.current.material.color.set(currentFillColor)
  }, [currentFillColor, selectedElementIds, id, isWorking])

  return (
    <group renderOrder={renderOrder} visible={opacity > 0 && visible}>
      {/* Guide lines between placed points */}
      {guideLine?.color || guideOutline?.color ? (
        <Polyline
          key={`${polygon.shape_id}-guidelines`}
          id={`${polygon.shape_id}-guidelines`}
          parentId={id}
          hideAnchors
          points={[...planePoints, ...(isOpenLoop ? [planePoints[0]] : [])].map((point) => point.toArray())}
          line={guideLineConfig}
          lineOutline={guideOutlineConfig}
          opacity={guideOpacity}
          isWorking={isWorking}
        />
      ) : null}

      {/* Plane mesh */}
      {planePoints.length > 2 && (
        <mesh
          ref={meshRef}
          onPointerOver={selectable && opacity > 0 && visible ? onPointerOver : undefined}
          onPointerOut={selectable && opacity > 0 && visible ? onPointerOut : undefined}
          onPointerDown={selectable && opacity > 0 && visible ? onPointerDown : undefined}
          onPointerUp={selectable && opacity > 0 && visible ? onPointerUp : undefined}
          position={planeOrigin}
          geometry={geometry}
          visible={!color || color !== 'transparent'}
          name={name}
          userData={{ shapeId: polygon.shape_id }}
        >
          {/* Use white when set as transparent since 'transparent' is not a valid color */}
          <meshBasicMaterial
            color={!color || color === 'transparent' ? 'white' : color}
            side={DoubleSide}
            transparent={transparent}
            opacity={opacity}
            depthTest={depthTest}
            depthWrite={depthWrite}
          />
        </mesh>
      )}

      {/* Guide line labels */}
      {guideLabelConfig && guideLabels.length
        ? guideLabels.map(({ id: guideLabelId, point, label: labelText }) => (
            <Html
              key={guideLabelId}
              position={point}
              style={{ transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none' }}
              zIndexRange={[1, 9]}
            >
              <StylesProvider>
                <Box
                  backgroundColor={guideLabelConfig.backgroundColor}
                  px={5}
                  fontSize={`${80 * labelFontSize}%`}
                  fontWeight="bold"
                  color={guideLabelConfig.color}
                  whiteSpace="nowrap"
                  borderWidth={guideLabelConfig.outlineWidth}
                  borderColor={guideLabelConfig.outlineColor || 'transparent'}
                  opacity={guideLabelConfig.opacity}
                >
                  {labelText}
                </Box>
              </StylesProvider>
            </Html>
          ))
        : null}

      {/* Label */}
      {!!label && meshRef.current && labelConfig ? (
        <Html
          position={labelPosition}
          style={{ transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none' }}
          zIndexRange={[1, 9]}
        >
          <StylesProvider>
            <Box
              backgroundColor={labelConfig.backgroundColor}
              px={2}
              // Convert the multiplier into a percentage-based font size for CSS styling
              fontSize={`${80 * (labelConfig.fontSize || 1)}%`}
              fontWeight="bold"
              color={labelConfig.color}
              whiteSpace="nowrap"
            >
              {labelConfig.message}
            </Box>
          </StylesProvider>
        </Html>
      ) : null}
    </group>
  )
}
